import { Routes } from '@angular/router';
import { provideTranslocoScope } from '@jsverse/transloco';
import { NavigationComponent } from './core/components/navigation/navigation.component';
import { HomeComponent } from './core/components/home/home.component';
import { RolesGuard } from './auth/guards/roles.guard';
import { ModuleAccessGuard } from './core/guards/module-access.guard';
import { AngularFireAuthGuard, redirectUnauthorizedTo } from '@angular/fire/compat/auth-guard';
import { AuthGuard } from './auth/guards/auth.guard';
import { orgRequiredGuard } from './core/guards/org-required.guard';
import { featureFlagGuard } from './core/guards/feature-flag.guard';
import { appInitResolver } from './core/resolvers/app-init.resolver';
import { PageNotFoundComponent } from './core/components/page-not-found/page-not-found.component';
import { AccessDeniedComponent } from './core/components/access-denied/access-denied.component';
import { AnonymousGuard } from './auth/guards/anonymous-guard.guard';
import { loginModuleRedirectGuard } from './auth/guards/login-module-redirect.guard';

const redirectUnauthorizedToLogin = () => redirectUnauthorizedTo(['login']);

export const appRoutes: Routes = [
  {
    path: 'module-transfer/:modulePath',
    loadComponent: () =>
      import('./auth/components/module-transfer/module-transfer.component').then((m) => m.ModuleTransferComponent),
  },
  {
    path: 'app',
    pathMatch: 'prefix',
    canActivate: [AuthGuard],
    resolve: {
      // ✅ Single master resolver that initializes everything in correct order
      appInit: appInitResolver,
    },
    component: NavigationComponent,
    children: [
      {
        path: 'module-transfer/:modulePath',
        canActivate: [orgRequiredGuard],
        loadComponent: () =>
          import('./auth/components/module-transfer/module-transfer.component').then((m) => m.ModuleTransferComponent),
      },
      {
        path: 'warehouse-orders',
        canActivate: [orgRequiredGuard, ModuleAccessGuard, RolesGuard],
        providers: [provideTranslocoScope({ scope: 'warehouse-orders', alias: 'warehouseOrders' })],
        loadChildren: () => import('./warehouse-orders/warehouse-orders.routes').then((m) => m.warehouseOrdersRoutes),
        data: {
          moduleName: 'warehouseOrders',
          roles: ['ADMIN', 'WAREHOUSEMANAGER', 'WAREHOUSESUPERVISOR', 'ACCOUNTANT', 'WAREHOUSECLERK'],
        },
      },
      {
        path: 'users-management',
        canActivate: [orgRequiredGuard, ModuleAccessGuard, RolesGuard],
        providers: [provideTranslocoScope({ scope: 'settings', alias: 'settings' })],
        loadChildren: () => import('./settings/settings.routes').then((m) => m.settingsRoutes),
        data: {
          moduleName: 'settings',
          roles: ['ADMIN'],
        },
      },
      {
        path: 'salesrep',
        canActivate: [orgRequiredGuard, ModuleAccessGuard, RolesGuard],
        providers: [provideTranslocoScope({ scope: 'salesrep', alias: 'salesrep' })],
        loadChildren: () => import('./salesrep/salesrep.routes').then((m) => m.salesrepRoutes),
        data: {
          moduleName: 'salesrep',
          roles: ['ADMIN', 'SALESREP'],
        },
      },
      {
        path: 'procurement',
        canActivate: [orgRequiredGuard, ModuleAccessGuard, RolesGuard],
        providers: [provideTranslocoScope({ scope: 'procurement', alias: 'procurement' })],
        loadChildren: () => import('./procurement/procurement.routes').then((m) => m.procurementRoutes),
        data: {
          moduleName: 'procurement',
          roles: ['ADMIN', 'PROCUREMENTMANAGER'],
        },
      },
      {
        path: 'expenses',
        canActivate: [orgRequiredGuard, ModuleAccessGuard],
        providers: [provideTranslocoScope({ scope: 'expenses', alias: 'expenses' })],
        loadChildren: () => import('./expenses/expenses.routes').then((m) => m.expensesRoutes),
        data: {
          moduleName: 'expenses',
        },
      },
      {
        path: 'profile',
        canActivate: [ModuleAccessGuard],
        providers: [provideTranslocoScope({ scope: 'profile', alias: 'profile' })],
        loadChildren: () => import('./profile/profile.routes').then((m) => m.profileRoutes),
        data: {
          moduleName: 'profile',
        },
      },
      {
        path: 'timesheets',
        canActivate: [orgRequiredGuard, ModuleAccessGuard],
        providers: [provideTranslocoScope({ scope: 'timesheets', alias: 'timesheets' })],
        loadChildren: () => import('./timesheets/timesheet.routes').then((m) => m.timesheetRoutes),
        data: {
          moduleName: 'timesheets',
        },
      },
      {
        path: 'marketing-campaigns',
        canActivate: [orgRequiredGuard, ModuleAccessGuard, RolesGuard],
        providers: [provideTranslocoScope({ scope: 'marketing-campaigns', alias: 'marketingCampaigns' })],
        loadChildren: () =>
          import('./marketing-campaigns/marketing-campaigns.routes').then((m) => m.marketingCampaignsRoutes),
        data: {
          moduleName: 'marketingCampaigns',
          roles: ['ADMIN', 'MASTERADMIN', 'MARKETING-MANAGER'],
        },
      },
      {
        path: 'contracts',
        canActivate: [orgRequiredGuard, ModuleAccessGuard, RolesGuard],
        providers: [provideTranslocoScope({ scope: 'contracts', alias: 'contracts' })],
        loadChildren: () => import('./contracts/contracts.routes').then((m) => m.contractsRoutes),
        data: {
          moduleName: 'contracts',
          roles: ['ADMIN', 'WAREHOUSEMANAGER'],
        },
      },
      {
        path: 'catalog-manager',
        canActivate: [orgRequiredGuard, ModuleAccessGuard, RolesGuard],
        providers: [provideTranslocoScope({ scope: 'catalog-manager', alias: 'catalogManager' })],
        loadChildren: () => import('./catalog-manager/catalog-manager.routes').then((m) => m.CATALOG_MANAGER_ROUTES),
        data: {
          moduleName: 'catalogManager',
          roles: ['ADMIN', 'WAREHOUSEMANAGER'],
        },
      },
      {
        path: 'tx-admin',
        canActivate: [orgRequiredGuard, ModuleAccessGuard, RolesGuard],
        providers: [provideTranslocoScope({ scope: 'tx-admin', alias: 'txAdmin' })],
        loadChildren: () => import('./tx-admin/tx-admin.routes').then((m) => m.txAdminRoutes),
        data: {
          moduleName: 'txAdmin',
          roles: ['ADMIN', 'MASTERADMIN'],
        },
      },
      {
        // Cross-brand organization administration ("Administracija organizacije").
        // Reuses the 'settings' module entitlement — already allowlisted on BOTH
        // brands — so no new module id or activeModules migration is needed.
        // The moved tx-admin tabs keep their txAdmin.* transloco scope.
        path: 'organization-admin',
        canActivate: [orgRequiredGuard, ModuleAccessGuard, RolesGuard],
        providers: [
          provideTranslocoScope({ scope: 'tx-admin', alias: 'txAdmin' }),
          provideTranslocoScope({ scope: 'organization-admin', alias: 'organizationAdmin' }),
        ],
        loadChildren: () =>
          import('./organization-admin/organization-admin.routes').then((m) => m.organizationAdminRoutes),
        data: {
          moduleName: 'settings',
          roles: ['MASTERADMIN'],
        },
      },
      {
        // Legacy aliases from the module's email-orders era — deep links in
        // mails and bookmarks keep working (same precedent as TAB_ALIASES).
        path: 'email-orders',
        redirectTo: 'order-inbox',
      },
      {
        path: 'email-orders/:messageId',
        redirectTo: 'order-inbox/:messageId',
      },
      {
        // Prijem porudžbina — operator surface for orders arriving by email.
        // Business-brand only ('orderInbox' lives in TEFTER_BRAND, never
        // TEFTER_TX_BRAND). canMatch (not canActivate) carries the feature
        // flag so a disabled flag means "no such route", not access-denied.
        path: 'order-inbox',
        canMatch: [featureFlagGuard('orderInbox')],
        canActivate: [orgRequiredGuard, ModuleAccessGuard, RolesGuard],
        providers: [
          provideTranslocoScope({ scope: 'order-inbox', alias: 'orderInbox' }),
          // Reused components keep their own scopes: tx-confidence-meter
          // (txOrders.*) on the review surface, app-mailbox-senders-panel
          // (organizationAdmin.*) on the settings tab — same precedent as
          // organization-admin's dual scope.
          provideTranslocoScope({ scope: 'tx-orders', alias: 'txOrders' }),
          provideTranslocoScope({ scope: 'organization-admin', alias: 'organizationAdmin' }),
        ],
        loadChildren: () => import('./order-inbox/order-inbox.routes').then((m) => m.orderInboxRoutes),
        data: {
          moduleName: 'orderInbox',
          roles: ['ADMIN', 'MASTERADMIN'],
        },
      },
      {
        path: 'tx-orders',
        canActivate: [orgRequiredGuard, ModuleAccessGuard, RolesGuard],
        providers: [provideTranslocoScope({ scope: 'tx-orders', alias: 'txOrders' })],
        loadChildren: () => import('./tx-orders/tx-orders.routes').then((m) => m.txOrdersRoutes),
        data: {
          moduleName: 'txOrders',
          roles: ['ADMIN', 'MASTERADMIN', 'VIEWER'],
        },
      },
      {
        path: 'access-denied',
        component: AccessDeniedComponent,
      },
      {
        path: 'home',
        component: HomeComponent,
      },
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'login',
    canActivate: [AnonymousGuard],
    loadComponent: () => import('./auth/components/auth-shell/auth-shell.component').then((m) => m.AuthShellComponent),
    children: [
      {
        path: 'email',
        pathMatch: 'full',
        loadComponent: () =>
          import('./auth/components/screens/email-entry/email-entry.component').then((m) => m.EmailEntryComponent),
      },
      {
        path: 'sms',
        pathMatch: 'full',
        loadComponent: () =>
          import('./auth/components/screens/phone-entry/phone-entry.component').then((m) => m.PhoneEntryComponent),
      },
      {
        path: 'sms/otp',
        pathMatch: 'full',
        loadComponent: () =>
          import('./auth/components/screens/otp-verify/otp-verify.component').then((m) => m.OtpVerifyComponent),
      },
      {
        // Bare /login: the guard always redirects to /login/email or /login/sms
        // based on the resolved module, so this component never actually renders.
        path: '',
        pathMatch: 'full',
        canActivate: [loginModuleRedirectGuard],
        loadComponent: () =>
          import('./auth/components/auth-shell/auth-shell.component').then((m) => m.AuthShellComponent),
      },
    ],
  },
  {
    // No guard intentionally — callback must work regardless of auth state.
    // Firebase auth state changes during the flow (sign-out then re-sign-in).
    path: 'auth/email-link',
    loadComponent: () =>
      import('./auth/components/email-link-callback/email-link-callback.component').then(
        (m) => m.EmailLinkCallbackComponent
      ),
  },
  {
    // No guard intentionally — this is the magic-link page an unregistered
    // supplier opens to decide an order (ADR 0035); there is no auth state at all.
    path: 'p/order',
    providers: [
      provideTranslocoScope({ scope: 'public-order', alias: 'publicOrder' }),
      provideTranslocoScope({ scope: 'tx-orders', alias: 'txOrders' }),
    ],
    loadChildren: () => import('./public-order/public-order.routes').then((m) => m.publicOrderRoutes),
  },
  {
    path: 'account',
    canActivate: [AngularFireAuthGuard],
    providers: [provideTranslocoScope({ scope: 'account', alias: 'account' })],
    loadChildren: () => import('./account/account.routes').then((m) => m.accountRoutes),
  },
  { path: '', redirectTo: '/app/home', pathMatch: 'full' },
  {
    path: '**',
    pathMatch: 'full',
    component: PageNotFoundComponent,
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin },
  },
];
